import React, { useContext, useEffect, useState } from 'react';
import { GetPersonUser } from '@esportal/portal-service';
import { Vehicle, VehicleStatus } from '@esportal/portal-service';
import {
	Container,
	TextAlignment,
	TokenTextAppearance,
	Text,
	ContainerPadding,
	Select,
	Layout,
	Breakpoints,
	styled,
} from '@volkswagen-onehub/components-core';
import { ServicesCitaTaller } from './components/services-cita-taller';
import { ServicesHeader } from './components/services-header';
import { isLongdriveActive, isWarrantyActive, isIDcareActive } from '../../helpers/servicesHelper';
import { ServicesCoberturas } from './components/services-cobertura';
import { ServicesDealer } from './components/services-dealer';
import { LayerHandleV2 } from '@volkswagen-onehub/layer-manager';
import {
	ServiceLayer,
	defaultServiceLayer,
	ServiceType,
	getServiceLayerData,
} from './detailLayer/services-layer-types';
import { ServicesDetailayer } from './detailLayer/services-detail-layer';
import { PortalContext } from '../../context/PortalContext';
import { contrastBackground } from '../../helpers/color-tokens';

const BackgroundPrimary = styled.div`
	background-color: ${contrastBackground};
`;

const BackgroundWrapper = styled.div`
	position: relative;
	width: 100%;
`;

const StyledSelect = styled(Select)`
	position: relative;
	z-index: 0;

	@media (max-width: 560px) {
		width: 180px;
	}

	@media (min-width: 561px) {
		min-width: 240px;
	}
`;

const HeaderContainer = styled(Container)`
	@media (max-width: 560px) {
		& div {
			display: block;
		}
	}
`;

const useIsMobile = (maxWidth = 560): boolean => {
	const [isMobile, setIsMobile] = useState(false);

	useEffect(() => {
		const mediaQuery = window.matchMedia(`(max-width: ${maxWidth}px)`);
		const handleChange = () => setIsMobile(mediaQuery.matches);

		handleChange();
		mediaQuery.addEventListener('change', handleChange);

		return () => mediaQuery.removeEventListener('change', handleChange);
	}, [maxWidth]);

	return isMobile;
};

export const MyServices = ({
	vehicles,
	userData,
}: {
	vehicles: Vehicle[];
	userData: GetPersonUser;
}): React.ReactElement => {
	const portalContext = useContext(PortalContext);
	const servicesLayerRef = React.useRef<LayerHandleV2<any>>(null);
	const [servicesLayerData, setServicesLayerData] = React.useState<{
		isVisible: boolean;
		data: ServiceLayer;
	}>({ isVisible: false, data: defaultServiceLayer });

	const openServicesLayer = (type: ServiceType) => {
		setServicesLayerData({
			isVisible: true,
			data: getServiceLayerData(type, selectedVehicle, portalContext),
		});
	};

	const filteredVehicles = vehicles
		.filter(
			(vehicle: Vehicle) =>
				vehicle.status == VehicleStatus.READY ||
				vehicle.status == VehicleStatus.TO_BE_CONFIRMED ||
				vehicle.status == VehicleStatus.TO_BE_DELETED ||
				vehicle?.licenseDate,
		)
		.sort((a, b) => new Date(b.licenseDate ?? 0).getTime() - new Date(a.licenseDate ?? 0).getTime());

	if (filteredVehicles.length < 1) return <></>;

	const isMobile = useIsMobile();
	const [selectedVehicle, setSelectedVehicle] = React.useState<Vehicle>(
		filteredVehicles.find(
			(vehicle: Vehicle) =>
				vehicle.longdrive ||
				vehicle.warranty?.flagVNGarantia ||
				vehicle.warranty?.flagVNGarantiaExtendida ||
				vehicle.comercialDenomination?.includes('ID.'),
		) || filteredVehicles[0],
	);

	const handleChange = (e: React.SyntheticEvent<HTMLInputElement> | React.SyntheticEvent<HTMLSelectElement>) => {
		const { value } = e.target as HTMLInputElement | HTMLSelectElement;
		const foundVehicle = filteredVehicles?.find((vehicle: Vehicle) => vehicle.frame === value);

		if (foundVehicle) {
			setSelectedVehicle(foundVehicle);
		}
	};

	//Llista de serveis
	const [accordionServices, setAccordionServices] = useState<{ id: string; isOpen: boolean }[]>([]);

	useEffect(() => {
		const services: { id: string; isOpen: boolean }[] = [];

		if (isLongdriveActive(selectedVehicle) || isWarrantyActive(selectedVehicle) || isIDcareActive(selectedVehicle)) {
			services.push({ id: 'coberturas', isOpen: false });
		}

		if (userData.portalUser.data.dealers?.pv?.commercialName) {
			services.push({ id: 'dealer', isOpen: false });
		}

		setAccordionServices(services);
	}, [selectedVehicle, userData]);

	const handleAccordionToggle = (id: string) => {
		setAccordionServices((prev) => {
			const updated = prev.map((service) => ({
				...service,
				isOpen: service.id === id ? !service.isOpen : false,
			}));

			return updated;
		});
	};

	const openIndex = accordionServices.findIndex((service) => service.isOpen);

	// Helper per saber si el accordion esta obert
	const isAccordionOpen = (id: string) => accordionServices.find((service) => service.id === id)?.isOpen ?? false;

	//Helper per saber l'index anterior del accordion seleccionat
	const isPreviousOpen = (id: string): boolean => {
		const index = accordionServices.findIndex((s) => s.id === id);
		return index === openIndex - 1;
	};

	return (
		<>
			<ServicesDetailayer
				servicesLayerData={servicesLayerData}
				setServicesLayerData={setServicesLayerData}
				servicesLayerRef={servicesLayerRef}
				vehicle={selectedVehicle}
				userData={userData}
			/>
			<Container wrap={'always'}>
				<Container stretchContent>
					<BackgroundWrapper data-testid="myServices-background">
						<Layout
							data-testid="myServices-layout"
							appearance={{
								[Breakpoints.default]: [
									{ name: '.', columns: 1 },
									{ name: 'a', columns: 22 },
									{ name: '.', columns: 1 },
								],
								[Breakpoints.b560]: [
									{ name: '.', columns: 1 },
									{ name: 'a', columns: 22 },
									{ name: '.', columns: 1 },
								],
								[Breakpoints.b960]: [
									{ name: '.', columns: 1 },
									{ name: 'a', columns: 22 },
									{ name: '.', columns: 1 },
								],
								[Breakpoints.b1280]: [
									{ name: '.', columns: 1 },
									{ name: 'a', columns: 22 },
									{ name: '.', columns: 1 },
								],
								[Breakpoints.b1920]: [
									{ name: '.', columns: 1 },
									{ name: 'a', columns: 22 },
									{ name: '.', columns: 1 },
								],
							}}
							allowOverflowingContent
						>
							<HeaderContainer stretchContent data-testid="HeaderContainer">
								<Container
									padding={{
										top: {
											[Breakpoints.default]: ContainerPadding.dynamic0250,
											[Breakpoints.b560]: ContainerPadding.dynamic0150,
										},
										bottom: {
											[Breakpoints.default]: ContainerPadding.static0,
											[Breakpoints.b560]: ContainerPadding.static0,
										},
									}}
									verticalAlign={'center'}
								>
									<Text appearance={TokenTextAppearance.headline500} textAlign={TextAlignment.left}>
										Tus <b>servicios</b>
									</Text>
								</Container>

								{vehicles.length > 1 && (
									<Container
										padding={{
											top: {
												[Breakpoints.default]: ContainerPadding.dynamic0250,
												[Breakpoints.b560]: ContainerPadding.dynamic0150,
											},
											bottom: ContainerPadding.dynamic0100,
											right: ContainerPadding.static100,
											left: ContainerPadding.static100,
										}}
										horizontalAlign={isMobile ? 'flex-start' : 'flex-end'}
										verticalAlign={'center'}
									>
										<StyledSelect
											label="Cambiar de coche"
											defaultValue={selectedVehicle.frame}
											onChange={handleChange}
											isFloating
											data-testid="Select!"
										>
											{filteredVehicles.map((vehicle: Vehicle) => (
												<option key={vehicle.frame} value={vehicle.frame}>
													{vehicle.comercialDenomination}
												</option>
											))}
										</StyledSelect>
									</Container>
								)}
							</HeaderContainer>
						</Layout>

						<ServicesHeader vehicle={selectedVehicle} />
					</BackgroundWrapper>
				</Container>

				<BackgroundPrimary>
					<Container
						stretchContent
						padding={{
							bottom: ContainerPadding.dynamic0100,
						}}
					>
						<Layout
							data-testid="myServices-layout"
							appearance={{
								[Breakpoints.default]: [
									{ name: '.', columns: 1 },
									{ name: 'a', columns: 22 },
									{ name: '.', columns: 1 },
								],
								[Breakpoints.b560]: [
									{ name: '.', columns: 1 },
									{ name: 'a', columns: 22 },
									{ name: '.', columns: 1 },
								],
								[Breakpoints.b960]: [
									{ name: '.', columns: 1 },
									{ name: 'a', columns: 22 },
									{ name: '.', columns: 1 },
								],
								[Breakpoints.b1280]: [
									{ name: '.', columns: 1 },
									{ name: 'a', columns: 22 },
									{ name: '.', columns: 1 },
								],
								[Breakpoints.b1920]: [
									{ name: '.', columns: 1 },
									{ name: 'a', columns: 22 },
									{ name: '.', columns: 1 },
								],
							}}
							allowOverflowingContent
						>
							{(isLongdriveActive(selectedVehicle) ||
								isWarrantyActive(selectedVehicle) ||
								isIDcareActive(selectedVehicle)) && (
								<ServicesCoberturas
									vehicle={selectedVehicle}
									isOpen={isAccordionOpen('coberturas')}
									isPreviousOpen={isPreviousOpen('coberturas')}
									onToggle={() => handleAccordionToggle('coberturas')}
									openServicesLayer={openServicesLayer}
								/>
							)}

							{userData.portalUser.data.dealers?.pv?.commercialName && (
								<ServicesDealer
									dealers={userData.portalUser.data.dealers.pv}
									isOpen={isAccordionOpen('dealer')}
									isPreviousOpen={isPreviousOpen('dealer')}
									onToggle={() => handleAccordionToggle('dealer')}
									openServicesLayer={openServicesLayer}
								/>
							)}

							<ServicesCitaTaller
								vehicleForm={selectedVehicle}
								dealers={userData.portalUser.data.dealers?.pv}
								portalContext={portalContext}
							/>
						</Layout>
					</Container>
				</BackgroundPrimary>
			</Container>
		</>
	);
};
